import type { AccordionSchemaType } from '@vedalib/editor/lib/brand'
import { SCHEMA } from '@vedalib/editor/lib/brand'
import { AccordionDisplayDivider, AccordionType } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import React, { useCallback, useMemo } from 'react'
import { getParent } from 'utils'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import { FormField } from 'components/formKit'
import type { IFieldConfig } from 'components/formKit'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumberArray,
  FieldSegmented,
  FieldSelect,
  FieldSlider,
} from 'components/formKit'
import type { IArrayOption } from 'components/formKit/fields/FieldNumberArray/FieldNumberArray'
import { useFormData } from 'components/formKit/hooks'
import { TileButton } from 'components/uiKit/Button'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { IDENTITY } from 'constants/commonConstans'
import { BrandTagEdited, BrandFieldShadow } from 'services/Branding/components'
import { BORDER_OPTIONS, OFFSET_ARRAY } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { AccordionFormBrandType } from './accordionTypes'
import accordionIndents from './assets/accordionIndents.svg'
import componentIndents from './assets/componentIndents.svg'
import contentIndents from './assets/contentIndents.svg'
import iconIndents from './assets/iconIndents.svg'

const useAccordionSplitted = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const data = useFormData()
  const { parent } = getParent<AccordionSchemaType>(name, data, 2)
  const hidden = getter(parent?.collapse.type) === AccordionType.SPLITTED
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowDivider = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const values = useFormData()
  const { parent } = getParent<AccordionSchemaType>(name, values, 2)
  const hidden = getter(parent?.divider.displayDivider) === AccordionDisplayDivider.HIDE
  return useMemo(() => ({ hidden }), [hidden])
}

const INTERVAL_ARRAY: IArrayOption[] = [
  {
    icon: (
      <div>
        <Icon name='builderInputUp' />
        <Icon name='builderInputDown' />
      </div>
    ),
  },
]

const ICON_INDENTS_ARRAY: IArrayOption[] = [
  { icon: <Icon name='builderInputForward' /> },
  { icon: <Icon name='builderInputBack' /> },
]

export const ACCORDION_STYLE_FIELDS = genSchema(SCHEMA.accordion.style, {
  collapse: {
    type: genBrandField({
      as: FieldSelect<AccordionType>,
      label: t('elements.accordion.schema.type'),
      props: {
        options: [
          {
            label: t('elements.accordion.schema.typeOptions.splitted'),
            value: AccordionType.SPLITTED,
          },
          {
            label: t('elements.accordion.schema.typeOptions.solid'),
            value: AccordionType.SOLID,
          },
        ],
      },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  select: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.accordion.tags.select'),
      props: { resettable: true, transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  border: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  divider: {
    displayDivider: genBrandField({
      as: FieldSelect<AccordionDisplayDivider>,
      label: t('elements.accordion.schema.displayDivider'),
      props: {
        options: [
          {
            label: t('elements.accordion.schema.displayDividerOptions.all'),
            value: AccordionDisplayDivider.ALL,
          },
          {
            label: t('elements.accordion.schema.displayDividerOptions.inner'),
            value: AccordionDisplayDivider.INNER,
          },
          {
            label: t('elements.accordion.schema.displayDividerOptions.first'),
            value: AccordionDisplayDivider.FIRST,
          },
          {
            label: t('elements.accordion.schema.displayDividerOptions.hide'),
            value: AccordionDisplayDivider.HIDE,
          },
        ],
      },
      useConfigs: [useAccordionSplitted],
    }),
    borderTopColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.accordion.schema.colorDivider'),
      useConfigs: [useBrandColorOptions, useAccordionSplitted, useShowDivider],
    }),
    borderTopWidth: genBrandField({
      as: FieldSlider,
      label: t('elements.accordion.schema.thicknessDivider'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useAccordionSplitted, useShowDivider],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      label: t('elements.accordion.schema.boxShadow'),
      as: BrandFieldShadow,
      useConfigs: [useThemeValueGetter],
    }),
  },
  icon: {
    position: genBrandField({
      as: FieldSegmented<'left' | 'right'>,
      label: t('elements.accordion.schema.place'),
      props: {
        options: [
          { label: <Icon name='builderArrowLeft' size={KitSize.S} />, value: 'left' },
          { label: <Icon name='builderArrowRight' size={KitSize.S} />, value: 'right' },
        ],
      },
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.accordion.schema.closed'),
      props: { fileType: FileUsageTypeEnum.image, preview: false },
      layout: 'vertical',
    }),
    backgroundOpen: genBrandField({
      as: FieldFileId,
      label: t('elements.accordion.schema.open'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image },
    }),
  },
  indents: {
    accordion: genBrandField({
      as: FieldNumberArray,
      label: t('elements.accordion.schema.accordionIndents'),
      layout: 'vertical',
      description: <img alt='accordion indents' src={accordionIndents} />,
      props: { options: OFFSET_ARRAY },
    }),
    content: genBrandField({
      as: FieldNumberArray,
      label: t('elements.accordion.schema.contentIndents'),
      layout: 'vertical',
      description: <img alt='content indents' src={contentIndents} />,
      props: { options: OFFSET_ARRAY },
    }),
    icon: genBrandField({
      as: FieldNumberArray,
      label: t('input.label.iconIndents'),
      layout: 'vertical',
      description: <img alt='icon indents' src={iconIndents} />,
      props: { options: ICON_INDENTS_ARRAY },
    }),
    gap: genBrandField({
      as: FieldNumberArray,
      label: t('elements.accordion.schema.componentInterval'),
      layout: 'vertical',
      description: <img alt='component indents' src={componentIndents} />,
      props: { options: INTERVAL_ARRAY },
    }),
  },
})

const AccordionFormStyle: AccordionFormBrandType = ({ edited, device, subConfigs, setForm }) => {
  const stylePath = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(stylePath, subConfigs, ACCORDION_STYLE_FIELDS)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom bottomSpace gap sides>
        <Typography weight='medium'>{t('elements.accordion.tags.accordion')}</Typography>
        <FormField config={schema.collapse.type} />
        <FormField config={schema.collapse.backgroundColor} />
        <FormField config={schema.hover.backgroundColor} />
        <FormField config={schema.select.backgroundColor} />
        <FormField config={schema.border.borderRadius} />
        <FormField config={schema.border.borderStyle} />
        <FormField config={schema.border.borderColor} />
        <FormField config={schema.border.borderWidth} />
        <FormField config={schema.divider.displayDivider} />
        <FormField config={schema.divider.borderTopColor} />
        <FormField config={schema.divider.borderTopWidth} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <Typography weight='medium'>{t('elements.accordion.tags.icon')}</Typography>
        <FormField config={schema.icon.position} />
        <FormField config={schema.icon.color} />
        <FormField config={schema.icon.backgroundImage} />
        <FormField config={schema.icon.backgroundOpen} />

        <Typography weight='medium'>{t('elements.accordion.tags.indents')}</Typography>
        <FormField config={schema.indents.accordion} />
        <FormField config={schema.indents.content} />
        <FormField config={schema.indents.icon} />
        <FormField config={schema.indents.gap} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>{' '}
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(AccordionFormStyle) as typeof AccordionFormStyle
